import { STOP_TYPE_DELIVERY, STOP_TYPE_PICKUP } from '#/entities/shipment'
import type {
  LocationData,
  LocationFormData,
  ShipmentFormData,
  VehicleItem,
  VehiclesFormData,
} from '#/features/order-create'
import { usPhoneToE164 } from '#/shared/lib/format'
import type {
  ShipmentDetail,
  ShipmentUpdate,
  ShipmentUpdateTransportType,
  StopUpsert,
  StopUpsertLocationType,
  VehicleUpsert,
} from '#/shared/api/generated/shipment/models'

function nullify(value: string): string | null {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function vehicleFormToUpsert(item: VehicleItem): VehicleUpsert {
  const year = Number.parseInt(item.year, 10)
  return {
    id: item.id,
    year: Number.isFinite(year) ? year : undefined,
    make: nullify(item.make) ?? undefined,
    model: nullify(item.model) ?? undefined,
    type: nullify(item.type),
    is_inoperable: item.inop,
    vin: nullify(item.vin),
    lot: nullify(item.lot),
  }
}

function diffVehicles(
  existing: ShipmentDetail['vehicles'] | undefined,
  next: VehicleItem[],
): VehicleUpsert[] {
  const nextIds = new Set(next.map((v) => v.id).filter(Boolean) as string[])
  const removed: VehicleUpsert[] = (existing ?? [])
    .filter((row) => !nextIds.has(row.id))
    .map((row) => ({ id: row.id, delete: true }))
  return [...next.map(vehicleFormToUpsert), ...removed]
}

function toLocationType(value: string): StopUpsertLocationType {
  const trimmed = value.trim()
  return trimmed.length > 0 ? (trimmed as StopUpsertLocationType) : null
}

function locationFormToStop(
  location: LocationData,
  role: typeof STOP_TYPE_PICKUP | typeof STOP_TYPE_DELIVERY,
): StopUpsert {
  return {
    id: location.id,
    type: role,
    location_type: toLocationType(location.type),
    business_name: nullify(location.businessName),
    address: nullify(location.address),
    city: nullify(location.city),
    state: nullify(location.state),
    zip: nullify(location.zip),
    notes: nullify(location.additionalNote),
    contact_name: nullify(location.contact),
    contact_phone: usPhoneToE164(location.phone),
    contact_mobile_phone: usPhoneToE164(location.mobile),
  }
}

function locationFormToCityZipStop(
  location: LocationData,
  role: typeof STOP_TYPE_PICKUP | typeof STOP_TYPE_DELIVERY,
): StopUpsert {
  return {
    id: location.id,
    type: role,
    city: nullify(location.city),
    state: nullify(location.state),
    zip: nullify(location.zip),
  }
}

function diffStops(
  existing: ShipmentDetail['stops'] | undefined,
  location: LocationFormData,
  compact: boolean,
): StopUpsert[] {
  const toStop = compact ? locationFormToCityZipStop : locationFormToStop
  const next = [
    toStop(location.origin, STOP_TYPE_PICKUP),
    toStop(location.destination, STOP_TYPE_DELIVERY),
  ]
  const nextIds = new Set(next.map((s) => s.id).filter(Boolean) as string[])
  const removed: StopUpsert[] = (existing ?? [])
    .filter((row) => !nextIds.has(row.id))
    .map((row) => ({ id: row.id, delete: true }))
  return [...next, ...removed]
}

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

function toDateOnlyOrNull(date: Date | undefined): string | null {
  if (!date) return null
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

interface BuildShipmentUpdateInput {
  shipment?: ShipmentDetail
  vehicles?: VehiclesFormData
  location?: LocationFormData
  shipmentFields?: ShipmentFormData
  compact?: boolean
}

function applyShipmentFields(
  body: ShipmentUpdate,
  fields: ShipmentFormData,
  compact: boolean,
): void {
  if (fields.trailer)
    body.transport_type = fields.trailer as ShipmentUpdateTransportType
  body.provider_id = fields.sourceId.length > 0 ? fields.sourceId : null
  body.first_available_date = toDateOnlyOrNull(fields.shipDate)
  body.instructions = nullify(fields.customerNote)
  if (compact) return
  body.estimated_pickup_at = toDateOnlyOrNull(fields.pickupDate)
  body.estimated_delivery_at = toDateOnlyOrNull(fields.deliveryDate)
  body.loadboard_instructions = nullify(fields.loadBoardNote)
  body.tag_ids = fields.tags
}

export function buildShipmentUpdate({
  shipment,
  vehicles,
  location,
  shipmentFields,
  compact = false,
}: BuildShipmentUpdateInput): ShipmentUpdate {
  const body: ShipmentUpdate = {}
  if (vehicles) {
    body.vehicles = diffVehicles(shipment?.vehicles, vehicles.vehicles)
  }
  if (location) {
    body.stops = diffStops(shipment?.stops, location, compact)
  }
  if (shipmentFields) {
    applyShipmentFields(body, shipmentFields, compact)
  }
  return body
}
